var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { HistoryType } from '@shared/utils/History/HistoryType';
import { ReloadHistoryParams } from '@shared/utils/History/ReloadHistoryParams';
import { Periods, TFInfo } from '@shared/utils/History/TFInfo';
import { SessionInfo } from '@shared/utils/History/SessionInfo';
import { TimeSpanPeriods } from '@shared/utils/Time/TimeSpan';
import { DataCache } from '../../DataCache';
import { CashItem } from '../../cache/History/CashItem';
import { TvHistoryConvertor } from '../Convertors/TvHistoryConvertor';
import { TvHistoryMetadata } from '../TradingViewPrimitives/TvHistoryMetadata';
import { TvHistorySubscriptionsContainer } from '../Containers/TvHistorySubscriptionsContainer';
import { TvTimeZoneHelper } from '../Helpers/TvTimeZoneHelper';
import { TvSymbolConvertor } from '../Convertors/TvSymbolConvertor';
import { TvInteriorIdCache } from '../Caches/TvInteriorIdCache';
import { TvSessionIdEnum } from '../TradingViewPrimitives/TvEnums';
import { TvHistoryResolutionHelper } from '../Helpers/TvHistoryResolutionHelper';
export class TvHistoryManager {
    constructor() {
        // #loadHistoryAbortController = null;
        this.account = null;
        this.historyConvertor = new TvHistoryConvertor();
        this.subscriptionsContainer = new TvHistorySubscriptionsContainer();
    }
    setCurrentAccount(account) {
        this.account = account;
        this.subscriptionsContainer.resetCacheByNewAccount();
    }
    getBars(symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const interiorId = TvInteriorIdCache.getInteriorId(symbolInfo.ticker);
            if (TvSymbolConvertor.isFakeSymbolName(interiorId)) {
                setTimeout(() => {
                    onHistoryCallback([], new TvHistoryMetadata(true));
                }, 0);
                return;
            }
            const instrument = DataCache.getInstrumentByName(interiorId);
            if (!instrument) {
                setTimeout(() => {
                    onErrorCallback(`[TvHistoryManager::getBars]: Instrument ${interiorId} not found`);
                }, 0);
                return;
            }
            // this.#loadHistoryAbortController?.abort();
            // this.#loadHistoryAbortController = new AbortController();
            // const signal = this.#loadHistoryAbortController.signal;
            const historyParams = this.GetHistoryParams(periodParams, resolution, symbolInfo.subsession_id, instrument);
            const isFirstDataRequest = periodParams.firstDataRequest;
            const loadHistory = historyParams.TimeFrameInfo.Periods % Periods.SECOND !== 0; // and TIC too
            const newCashItem = yield CashItem.CreateWithHistory(instrument, historyParams, 
            /* signal */ null, isFirstDataRequest, loadHistory, isFirstDataRequest);
            if (!newCashItem) {
                setTimeout(() => {
                    onHistoryCallback([], new TvHistoryMetadata(true));
                }, 0);
                return;
            }
            const period = newCashItem.TimeFrameInfo.Periods;
            const baseIntervals = newCashItem.FNonEmptyCashArray;
            const timeOffset = (_b = (_a = instrument === null || instrument === void 0 ? void 0 : instrument.Session) === null || _a === void 0 ? void 0 : _a.TimeOffset) !== null && _b !== void 0 ? _b : 0;
            const timeZone = TvTimeZoneHelper.getTimeZoneForTV((_c = instrument === null || instrument === void 0 ? void 0 : instrument.Session) === null || _c === void 0 ? void 0 : _c.TimeZoneID);
            const tvBars = this.historyConvertor.convertBaseIntervalsToTvBars(baseIntervals, period, timeOffset, timeZone);
            if (isFirstDataRequest) {
                this.subscriptionsContainer.insertCashItem(interiorId, resolution, newCashItem, timeOffset, timeZone);
            }
            else {
                newCashItem.Dispose();
            }
            const requestedBarsCount = periodParams.countBack;
            const isNoData = tvBars.length < requestedBarsCount;
            if (isNoData) {
                console.log(`[TvHistoryManager::getBars]: No data for ${instrument.ShortName} ${resolution} ${this.convertTime(periodParams.from)} - ${this.convertTime(periodParams.to)}`);
            }
            if (tvBars.length > requestedBarsCount) {
                // на всякий случай самый первый бар, который потенциально может быть неправильно агрегирован
                tvBars.shift();
            }
            onHistoryCallback(tvBars, new TvHistoryMetadata(isNoData));
        });
    }
    // Изначально я хотел создать отдельный класс для подписок
    // Но посчитал это затратным потому что нужно создавать отдельный кэшайтем и реквестить один бар истории
    // Это дополнительный запрос истории и накладные расходы
    subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback) {
        const ticker = TvInteriorIdCache.getInteriorId(symbolInfo.ticker);
        if (TvSymbolConvertor.isFakeSymbolName(ticker)) {
            return;
        }
        this.subscriptionsContainer.subscribeBars(ticker, resolution, subscriberUID, onRealtimeCallback, onResetCacheNeededCallback);
    }
    unsubscribeBars(subscriberUID) {
        this.subscriptionsContainer.unsubscribeBars(subscriberUID);
    }
    GetHistoryParams(periodParams, resolution, subsessionId, instrument) {
        // hsa: в реквесте нужно указывать данные залогиненного аккаунта
        const acc = DataCache.MainAccountNew || this.account;
        let rigthBorder;
        if (periodParams.firstDataRequest) {
            const now = new Date(); // like on chart
            rigthBorder = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        }
        else {
            rigthBorder = this.convertTime(periodParams.to);
        }
        const tfInfo = this.getTimeFrameInfo(resolution, subsessionId, instrument);
        const basePeriod = Periods.TranslateToParsePeriod(tfInfo.Periods);
        const leftBorderInYears = basePeriod === Periods.DAY ? 10 : 3;
        let leftBorder = this.convertTime(periodParams.from);
        leftBorder.setFullYear(leftBorder.getFullYear() - leftBorderInYears); // 100 years ago?
        if (leftBorder.getTime() < 0) {
            leftBorder = new Date(0);
        }
        if (rigthBorder.getTime() < 0) {
            rigthBorder = new Date(0);
        }
        // correct right date border, beacuse this border include existing bar
        const rightDateInMillis = rigthBorder.getTime();
        rigthBorder.setTime(rightDateInMillis - 1);
        // почему-то сервер иногда возвращает на 1 бар меньше, хотя история еще есть, поэтому запрашиваю еще один
        // также последний бар может быть неполный (неправильно сагрегированный) из-за приблизительного указания количества баров
        // такой бар удаляется в конце
        const requestedBarsCount = periodParams.countBack + 2;
        const rhp = new ReloadHistoryParams();
        rhp.updateWithProps({
            instrument,
            account: acc,
            isIgnoreAccount: true,
            TimeFrameInfo: tfInfo,
            ToTime: rigthBorder,
            FromTime: null,
            count: requestedBarsCount,
            UseDefaultInstrumentHistoryType: true
        });
        return rhp;
    }
    convertTime(timeInSeconds) {
        const millisecondsInSecond = TimeSpanPeriods.TicksPerSecond;
        const timeInTicks = timeInSeconds * millisecondsInSecond;
        return new Date(timeInTicks);
    }
    getTimeFrameInfo(resolution, subsessionId, instrument) {
        const tfInfo = new TFInfo();
        tfInfo.Periods = TvHistoryResolutionHelper.convertResolutionToPeriod(resolution);
        tfInfo.HistoryType = this.getHistoryType(instrument);
        tfInfo.SessionInfo = new SessionInfo(subsessionId === TvSessionIdEnum.regular);
        tfInfo.Plan = DataCache.GetSpreadPlan(this.account);
        return tfInfo;
    }
    getHistoryType(instrument) {
        return instrument ? instrument.DefaultChartHistoryType : HistoryType.BID;
    }
    dispose() {
        this.subscriptionsContainer.dispose();
    }
}
